import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Box, Typography, useTheme } from "@mui/material";
import AppState from "../../store/AppState";
import {
  selectIsOverlaySelectedByType,
  selectOverlay,
  selectInventoryAttribute,
} from "../../store/overlay/OverlaySelectors";
import {
  selectProducts,
  selectProductColors,
} from "../../store/product/ProductSelectors";
import { selectCurrencyCode } from "../../store/store-list/StoreListSelectors";
import { OVERLAY_TABS } from "../../utils/Constants";
import { getProductTilePriceDisplay } from "../../utils/ProductUtil";
import { useStyles } from "./InventoryOverlayStyles";

interface Props {
  productId: string;
}

interface InventoryRow {
  valueLabel: string;
  stock: number;
}

const InventoryOverlay: React.FC<Props> = ({ productId }) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const isInventoryOverlaySelected = useSelector((state: AppState) =>
    selectIsOverlaySelectedByType(state, OVERLAY_TABS.INVENTORY_OVERLAY),
  );
  const selectedOverlay = useSelector(selectOverlay);
  const selectedAttribute = useSelector(selectInventoryAttribute);
  const allProducts = useSelector(selectProducts);
  const productsColorData = useSelector(selectProductColors);
  const currencyCode = useSelector(selectCurrencyCode);

  const product = allProducts[productId];
  // Distinguish "variants not yet loaded" (undefined) from "loaded but
  // empty" ([]). Undefined drives the "Loading..." state.
  const variants = product?.variants;
  const isLoading = variants === undefined;

  const swatchByName = useMemo(() => {
    const colors = productsColorData[productId]?.colors ?? [];
    const map = new Map<string, string>();
    for (const c of colors) {
      if (c?.name && c?.swatchImage) {
        map.set(c.name.toLowerCase(), c.swatchImage);
      }
    }
    return map;
  }, [productsColorData, productId]);

  const name = product?.name ?? "";
  const code = product?.code ?? "";
  const priceDisplay = getProductTilePriceDisplay(product, currencyCode);
  const imageUrl = product?.thumbnail;

  /**
   * Walk the product's variants, find each variant's option that
   * matches the selected attribute label, and sum stock per matched
   * value. Published-only — unpublished  variants are skipped.
   */
  const rows: InventoryRow[] = useMemo(() => {
    if (!Array.isArray(variants) || variants.length === 0) return [];
    if (!selectedAttribute) return [];

    const stockByValue = new Map<string, number>();
    for (const variant of variants) {
      if (variant?.isPublished === false) continue;
      const options = variant?.options;
      if (!Array.isArray(options)) continue;
      const match = options.find((o) => o?.optionLabel === selectedAttribute);
      if (!match?.valueLabel) continue;
      const existing = stockByValue.get(match.valueLabel) ?? 0;
      stockByValue.set(match.valueLabel, existing + (variant.stock ?? 0));
    }

    const entries = Array.from(stockByValue.entries()).map(
      ([valueLabel, stock]) => ({ valueLabel, stock }),
    );

    const allNumeric = entries.every((r) => {
      const n = Number(r.valueLabel);
      return Number.isFinite(n) && r.valueLabel.trim() !== "";
    });
    if (allNumeric) {
      entries.sort((a, b) => {
        const na = Number(a.valueLabel);
        const nb = Number(b.valueLabel);
        const aWhole = Number.isInteger(na);
        const bWhole = Number.isInteger(nb);
        if (aWhole !== bWhole) return aWhole ? -1 : 1;
        return na - nb;
      });
    }
    return entries;
  }, [variants, selectedAttribute]);

  if (
    !isInventoryOverlaySelected ||
    selectedOverlay !== OVERLAY_TABS.INVENTORY_OVERLAY
  ) {
    return null;
  }

  return (
    <Box data-testid="inventory-overlay" sx={classes.container}>
      {/* Header: image left, name + price/partnumber stacked right. */}
      <Box sx={classes.headerRow}>
        {imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt={name}
            sx={classes.image}
          />
        ) : (
          <Box sx={classes.imagePlaceholder} />
        )}
        <Box sx={classes.details}>
          <Typography variant="title" title={name} sx={classes.nameText}>
            {name}
          </Typography>
          <Typography variant="title2" sx={classes.priceLine}>
            {priceDisplay}
            {priceDisplay && code ? " - " : ""}
            {code}
          </Typography>
        </Box>
      </Box>

      {isLoading ? (
        <Typography
          variant="title2"
          data-testid="inventory-overlay-loading"
          sx={classes.loading}
        >
          Loading...
        </Typography>
      ) : (
        <Box component="table" sx={classes.table}>
          <thead>
            <tr>
              <Box component="th" sx={classes.tableHeaderEmpty} />
              <Box component="th" sx={classes.tableHeaderLeft}>
                <Typography variant="gridTitle">
                  {selectedAttribute || ""}
                </Typography>
              </Box>
              <Box component="th" sx={classes.tableHeaderRight}>
                <Typography variant="gridTitle">Inv</Typography>
              </Box>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const swatchSrc =
                swatchByName.get(row.valueLabel.toLowerCase()) || "";
              return (
                <tr
                  key={`${productId}-${selectedAttribute}-${row.valueLabel}`}
                >
                  <Box component="td" sx={classes.swatchCell}>
                    {swatchSrc ? (
                      <Box
                        component="img"
                        src={swatchSrc}
                        alt={row.valueLabel}
                        sx={classes.swatchImg}
                      />
                    ) : null}
                  </Box>
                  <Box
                    component="td"
                    title={row.valueLabel}
                    sx={classes.valueCell}
                  >
                    <Typography variant="title2">{row.valueLabel}</Typography>
                  </Box>
                  <Box component="td" sx={classes.stockCell}>
                    <Typography variant="scaleNumbers">{row.stock}</Typography>
                  </Box>
                </tr>
              );
            })}
          </tbody>
        </Box>
      )}
    </Box>
  );
};

export default React.memo(InventoryOverlay);
